import PropTypes from 'prop-types';
import {
  PiCheckCircleFill,
  PiInfoFill,
  PiWarningCircleFill,
} from 'react-icons/pi';

import { cn } from '#/lib/utils';

const NOTICE_FIELD_ROWS = [
  { key: 'stockNo', label: '庫存編號' },
  { key: 'mtrNo', label: '物料代號' },
  { key: 'mtrName', label: '物料名稱' },
  { key: 'qty', label: '數量', tabular: true },
];

const CHIP_BY_KIND = {
  success: {
    labelClassName: 'text-green-100',
    chipClassName: 'bg-green-100 text-green-900',
  },
  notFound: {
    labelClassName: 'text-red-100',
    chipClassName: 'bg-red-100 text-red-700',
  },
  duplicate: {
    labelClassName: 'text-primary-100',
    chipClassName: 'bg-slate-700 text-white',
  },
};

function displayNoticeValue(value) {
  if (value === '' || value == null) {
    return '—';
  }
  return value;
}

const ScanNoticeFields = ({ notice, kind }) => {
  const tone = CHIP_BY_KIND[kind] || CHIP_BY_KIND.success;

  return (
    <div className="min-w-0 space-y-2">
      {NOTICE_FIELD_ROWS.map((field) => (
        <p key={field.key} className="tracking-wider">
          <span
            className={cn(
              'inline-block w-20 shrink-0',
              tone.labelClassName,
            )}
          >
            {field.label}
          </span>
          {' '}
          <span
            className={cn(
              'px-1.5 py-0.5 rounded font-mono font-bold',
              field.tabular && 'tabular-nums',
              tone.chipClassName,
            )}
          >
            {displayNoticeValue(notice[field.key])}
          </span>
        </p>
      ))}
    </div>
  );
};

ScanNoticeFields.propTypes = {
  kind: PropTypes.oneOf(['success', 'notFound', 'duplicate']).isRequired,
  notice: PropTypes.shape({
    stockNo: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

/**
 * 盤點掃碼上方提示：成功（綠）／未找到（深紅）／重複（深灰）；單一 notice，四欄統一。
 */
const StocktakeScanNotice = ({ notice }) => {
  if (!notice?.kind || !notice?.stockNo) {
    return null;
  }

  const isSuccess = notice.kind === 'success';
  const isNotFound = notice.kind === 'notFound';
  const isDuplicate = notice.kind === 'duplicate';
  if (!isSuccess && !isNotFound && !isDuplicate) {
    return null;
  }

  return (
    <div
      className={cn(
        'mb-4 p-5 rounded-md border font-semibold tracking-wide',
        'flex items-start gap-2',
        isSuccess && 'border-green-900 bg-green-500 text-green-50',
        isNotFound && 'border-red-900 bg-red-500 text-red-50',
        isDuplicate && 'border-primary-900 bg-primary-500 text-white',
      )}
      role="status"
    >
      {isSuccess ? (
        <PiCheckCircleFill className="mt-0.5 size-6 shrink-0 text-green-50" aria-hidden />
      ) : null}
      {isNotFound ? (
        <PiWarningCircleFill className="mt-0.5 size-6 shrink-0 text-red-50" aria-hidden />
      ) : null}
      {isDuplicate ? (
        <PiInfoFill className="mt-0.5 size-6 shrink-0 text-sky-50" aria-hidden />
      ) : null}
      <div className="min-w-0 space-y-2">
        <p className="font-semibold text-lg">
          {isSuccess ? '庫存編號新增成功！' : null}
          {isNotFound ? '未找到庫存資訊' : null}
          {isDuplicate ? '此庫存已在盤點清單' : null}
        </p>
        <ScanNoticeFields notice={notice} kind={notice.kind} />
      </div>
    </div>
  );
};

StocktakeScanNotice.propTypes = {
  notice: PropTypes.shape({
    kind: PropTypes.oneOf(['success', 'notFound', 'duplicate']),
    stockNo: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default StocktakeScanNotice;
